import React, { memo, useCallback, useRef } from "react"
import { useRowEditor } from "../context/QuoteEditorContext"
import { useSearchClass, useSearching, useScrollRoot } from "../context/TreeViewContext"
import useNearScroll from "../hooks/useNearScroll"
import useSortable from "../hooks/useSortable"
import AggregateBlock from "./AggregateBlock"
import ComponentBlock from "./ComponentBlock"
import DescriptionBlock from "./DescriptionBlock"
import { hasLiveChildren } from "../helpers/treeMath"
import { quantityOf } from "../helpers/formatting"
import { trailBelow } from "@react/shared/ui/TreeGuides"

const SortableChildren = ({ parentUid, children }) => {
  const { dispatch } = useRowEditor()

  const handleReorder = useCallback((fromIndex, toIndex) => {
    dispatch({
      type: "REORDER_NODE",
      payload: { parentUid, fromIndex, toIndex },
    })
  }, [dispatch, parentUid])

  const ref = useSortable(handleReorder, { draggable: ".qb-node" })

  return (
    <div className="qb-children" ref={ref}>
      {children}
    </div>
  )
}

// Lots past the first few render when scrolled near; the placeholder keeps the scrollbar's length.
const EAGER_LOTS = 3
const ROW_HEIGHT = 44

const visibleRows = node => (node.children || []).reduce((count, child) => {
  if (child._destroy) return count
  return count + 1 + (child._expanded === false ? 0 : visibleRows(child))
}, 0)

const LotBody = ({ node, eager, children }) => {
  const ref     = useRef(null)
  const root    = useScrollRoot()
  const reached = useNearScroll(ref, root, { initial: eager, force: eager })
  if (reached) return children
  return <div ref={ref} aria-hidden="true" style={{ height: visibleRows(node) * ROW_HEIGHT }} />
}

// A deleted line stays in the tree until the save: it must not keep the branch
// of the one before it open.
const placeChildren = (node, depth, trail, last) => {
  const live    = (node.children || []).filter(child => !child._destroy)
  const lastUid = live[live.length - 1]?._uid
  return { trail: trailBelow(depth, trail, last), isLast: child => child._uid === lastUid }
}

// `depth` places a row in the tree of the whole quote; `nesting` counts the sub-ouvrages above a line.
const NodeRenderer = ({ node, path = [], depth = 0, nesting = 0, trail = [], last = false, sectionUid = null, parentQty = 1 }) => {
  const hit       = useSearchClass(node?._uid)
  const searching = useSearching()
  if (!node || node._destroy) return null
  // A search opens folded nodes, so their rows must render then.
  const showChildren = node._expanded !== false || searching

  if (node.type === "service" && hasLiveChildren(node)) {
    const collapsed = node._expanded === false
    const nodeClass = ["qb-node component", collapsed ? "collapsed" : "", hit]
      .filter(Boolean).join(" ")
    const childQty  = parentQty * (parseFloat(node.quantity) || 0)
    const below     = placeChildren(node, depth, trail, last)
    return (
      <div className={nodeClass} data-uid={node._uid}>
        <ComponentBlock node={node} parentQty={parentQty} sectionUid={sectionUid} depth={depth} nesting={nesting} trail={trail} last={last} />
        <SortableChildren parentUid={node._uid}>
          {showChildren && node.children.map(child => (
            <MemoNodeRenderer
              key={child._uid}
              node={child}
              depth={depth + 1}
              nesting={nesting + 1}
              trail={below.trail}
              last={below.isLast(child)}
              sectionUid={sectionUid}
              parentQty={childQty}
            />
          ))}
        </SortableChildren>
      </div>
    )
  }

  if (node.type === "service") {
    return (
      <div className={`qb-node component ${hit}`} data-uid={node._uid}>
        <ComponentBlock node={node} parentQty={parentQty} sectionUid={sectionUid} depth={depth} nesting={nesting} trail={trail} last={last} />
      </div>
    )
  }

  if (node.type === "descriptive") {
    return (
      <div className={`qb-node descriptive ${hit}`} data-uid={node._uid}>
        <DescriptionBlock node={node} sectionUid={sectionUid} depth={depth} trail={trail} last={last} />
      </div>
    )
  }

  const isSection = node.type === "group"
  const variant   = depth === 0 ? "group" : "subgroup"
  const collapsed = node._expanded === false

  // Not a bare `group`: that is Tailwind's hover group, and would reveal every row's handle at once.
  const nodeClass = ["qb-node", `qb-${variant}`, collapsed ? "collapsed" : "", hit]
    .filter(Boolean).join(" ")

  const code = path.join(".")

  const anchorSectionUid = isSection ? node._uid : sectionUid

  const hasLineChildren  = isSection && (node.children || []).some(c => c.type === "service" && !c._destroy)
  const childQtyFactor   = hasLineChildren ? quantityOf(node.quantity) : 1
  const below            = placeChildren(node, depth, trail, last)

  // A deleted line takes no number: it will not be sent to the server.
  let childCode = 0
  const childNodes = !showChildren ? null : (node.children || []).map(child => {
    const numbered = child.type !== "descriptive" && !child._destroy
    if (numbered) childCode += 1
    return (
      <MemoNodeRenderer
        key={child._uid}
        node={child}
        path={numbered ? [...path, childCode] : []}
        depth={depth + 1}
        trail={below.trail}
        last={below.isLast(child)}
        sectionUid={anchorSectionUid}
        parentQty={childQtyFactor}
      />
    )
  })

  return (
    <div className={nodeClass} data-uid={node._uid}>
      <AggregateBlock node={node} variant={variant} code={code} depth={depth} trail={trail} last={last}>
        <SortableChildren parentUid={node._uid}>
          {depth === 0 && childNodes ? (
            <LotBody node={node} eager={searching || (path[0] || 0) <= EAGER_LOTS}>{childNodes}</LotBody>
          ) : childNodes}
        </SortableChildren>
      </AggregateBlock>
    </div>
  )
}

const sameArray = (a, b) =>
  a === b || (a.length === b.length && a.every((value, index) => value === b[index]))

// `path` and `trail` are rebuilt by every parent render, so they are compared by value.
const sameProps = (prev, next) =>
  prev.node       === next.node       &&
  prev.depth      === next.depth      &&
  prev.nesting    === next.nesting    &&
  prev.last       === next.last       &&
  prev.sectionUid === next.sectionUid &&
  prev.parentQty  === next.parentQty  &&
  sameArray(prev.path  || [], next.path  || []) &&
  sameArray(prev.trail || [], next.trail || [])

const MemoNodeRenderer = memo(NodeRenderer, sameProps)

export default MemoNodeRenderer
